import React, { useState, Fragment, useEffect, useLayoutEffect } from "react";
import { createInitialFieldModel, FieldModel, areAllValid } from "../../shared/model/field-model";
import { createPasswordValidator } from "../../helpers/create-validators";
import { InputWrapper } from "../../parts/input-wrapper/InputWrapper";
import { FormContainer } from "../../parts/form-container/FormContainer";
import { Button } from "../../parts/button/Button";
import passwordImage from "../../assets/images/simplyhealth_logo.png";
import { createFieldHandlers } from "../../helpers/create-field-handlers";
import { forgotPasswordSubmit } from "@simplyhealth/iam_client_lib";
import { createInitialPageModel, PageModel, PageStatus } from "../../shared/model/page-model";
import { Progress } from "../../parts/progress/Progress";
import { createElementName } from "../../shared/helpers/element.naming";
import { ErrorMessages } from "../../parts/error-messages/ErrorMessages";
import { isProcessing, isCompleted, isErrored } from "../../helpers/display-block-logic";
import { fromQueryParameters } from "../../helpers/query-parameters-helper";
import { updatePageModelForStatusChangeReducer } from "./helpers/confirm-forgot-password-page-model-reducer";
import { createForgotPasswordSubmitRequest } from "./helpers/confirm-forgot-password-request-helper";
import { createUIMessageAndTitleFromResponseCodes } from "./helpers/confirm-forgot-password-response-code-helpers";
import { updatePageModelReducer } from "../../shared/model/page-model-reduers";
import { ensureString } from "../../helpers/string-helpers";
import { sendPageVisitAnalytics, ANALYTICS_NAMES } from "../../helpers/analytics-helpers";

export const ConfirmForgotPassword = () => {
  const PAGE_NAME = "confirmforgotpassword";

  const [passwordState, setPasswordState] = useState<FieldModel>(createInitialFieldModel());

  const [pageStatus, setPageStatus] = useState<PageStatus>("Ready");
  const [pageModel, setPageModel] = useState<PageModel>(createInitialPageModel());

  const [onPasswordChange, onPasswordBlur, invokePasswordValidation] = createFieldHandlers(
    passwordState,
    setPasswordState,
    createPasswordValidator()
  );

  useLayoutEffect(() => {
    if (!fromQueryParameters().doAllVerificationExist()) {
      setPageStatus(() => "Missing Query Parameters");
    }
  }, []);

  useEffect(() => {
    sendPageVisitAnalytics(ANALYTICS_NAMES.confirmForgottenPassword);
  }, [])

  useEffect(() => {
    setPageModel(pm => updatePageModelForStatusChangeReducer(pm, pageStatus));
  }, [pageStatus]);

  const onSubmitClick = () => {
    switch (pageStatus) {
      case "Ready":
        sendRequest();
        break;
      case "Sending":
        // Do nothing
        break;
      case "Succeeded":
        // Do nothing
        break;
      case "Failed":
        setPageStatus("Ready");
        break;
    }
  };
  const sendRequest = () => {
    invokePasswordValidation();
    if (areAllValid(passwordState)) {
      setPageStatus("Sending");
      const request = createForgotPasswordSubmitRequest(
        ensureString(fromQueryParameters().getUsername()),
        ensureString(fromQueryParameters().getConfirmationCode()),
        passwordState.value
      );
      forgotPasswordSubmit(request)
        .then(response => {
          if (response.status === "succeeded") {
            setPageStatus("Succeeded");
          } else {
            const [informationMessages, title] = createUIMessageAndTitleFromResponseCodes(
              response.code,
              response.supportCode
            );
            setPageModel(updatePageModelReducer(pageModel, { informationMessages, title }));
            setPageStatus("Failed");
          }
        })
        .catch(error => {
          const [informationMessages, title] = createUIMessageAndTitleFromResponseCodes("", "");
          setPageModel(updatePageModelReducer(pageModel, { informationMessages, title }));
          setPageStatus("Failed");
        });
    }
  };

  return (
    <FormContainer
      pageName={PAGE_NAME}
      title={pageModel.title}
      messages={pageModel.informationMessages}
      image={<img src={passwordImage} alt="" />}
      fields={
        isProcessing(pageStatus) ? (
          <Fragment>
            <Progress />
          </Fragment>
        ) : isCompleted(pageStatus) ? (
          <Fragment />
        ) : isErrored(pageStatus) ? (
          <Fragment>
            <ErrorMessages
              name={createElementName(PAGE_NAME, "submit_errors", "errors")}
              messages={pageModel.errorMessages}
            />
          </Fragment>
        ) : (
          <Fragment>
            <InputWrapper
              pageName={PAGE_NAME}
              name="password"
              caption="New Password"
              inputType="password"
              autoCompleteType="new-password"
              fieldState={passwordState}
              shouldDisplayRequired
              onInputChange={onPasswordChange}
              onInputBlur={onPasswordBlur}
            />
          </Fragment>
        )
      }
      action={
        pageModel.showButton && (
          <Button pageName={PAGE_NAME} name="submit" caption={pageModel.buttonTitle} onClick={onSubmitClick} />
        )
      }
    />
  );
};
